import { PlusOutlined } from "@ant-design/icons"
import { Card, Flex, Typography, Upload, type UploadProps } from "antd"
import React, { forwardRef, memo, useMemo } from "react"

interface ProjectViewAlbumAttachmentsUploadProps {
  allowedExtensions: string[]
  maxFileSize?: number
  onBeforeUpload?: (file: File) => boolean
}

export const ProjectViewAlbumAttachmentsUpload = memo(
  forwardRef<HTMLInputElement, ProjectViewAlbumAttachmentsUploadProps>(
    function ProjectViewAlbumAttachmentsUpload(
      { allowedExtensions, maxFileSize, onBeforeUpload },
      ref
    ) {
      const accept = useMemo(
        () => allowedExtensions.map((ext) => `.${ext}`).join(","),
        [allowedExtensions]
      )

      const uploadProps: UploadProps = useMemo(
        () => ({
          showUploadList: false,
          multiple: true,
          accept,
          beforeUpload: onBeforeUpload as UploadProps["beforeUpload"]
        }),
        [accept, onBeforeUpload]
      )

      return (
        <>
          {/* Hidden input for programmatic trigger */}
          <input
            ref={ref}
            type="file"
            multiple
            accept={accept}
            className="hidden"
            onChange={(e) => {
              const files = Array.from(e.target.files || [])
              files.forEach((file) => onBeforeUpload?.(file))
              e.target.value = ""
            }}
          />

          {/* Upload Card */}
          <Upload
            {...uploadProps}
            rootClassName="h-full [&_.ant-upload]:size-full"
          >
            <Card
              hoverable
              className="h-full min-h-[120px] flex items-center justify-center"
            >
              <Flex vertical align="center" gap={8}>
                <PlusOutlined className="text-2xl" />
                <Typography.Text
                  type="secondary"
                  className="text-xs text-center"
                >
                  Supported: {allowedExtensions.join(", ")}
                  <br />
                  Max size: {maxFileSize}MB
                </Typography.Text>
              </Flex>
            </Card>
          </Upload>
        </>
      )
    }
  )
)
